import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import {
  type EpocusEncounter,
  RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
  useRxCollectionEpocusEncounter,
} from "$global-providers/rxdb-provider/collections/epocus-encounter";
import { useRxCollectionEpocusExam } from "$global-providers/rxdb-provider/collections/epocus-exam";
import { useServerTime } from "$global-providers/server-time-provider/provider";
import { toast } from "$global-providers/toast-provider";
import { useUser } from "$global-providers/user-provider/provider";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Box,
  Button,
  CircularProgress,
  styled,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import * as Sentry from "@sentry/react";
import { type FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRxData } from "rxdb-hooks";
import { encounterDisplayLabel } from "../_lib/encounter-display-label";
import { STAT_ICON_COMPONENTS, UltrasoundScanner } from "../_lib/epocus-icons";
import {
  EPOCUS_BORDER,
  EPOCUS_ICON_ACTIVE,
  EPOCUS_ORANGE,
  EPOCUS_ORANGE_HOVER,
  EPOCUS_PRIMARY,
  EPOCUS_STATUS_IMPORTED_BG,
  EPOCUS_STATUS_IMPORTED_TEXT,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_TO_DOC_BG,
  EPOCUS_TO_DOC_BORDER,
  EPOCUS_WHITE,
  rem,
} from "../_lib/epocus-palette";
import {
  type EpocusRoleVariant,
  useRoleVariant,
} from "../_lib/epocus-role-variant";
import { epocusTheme } from "../_lib/epocus-theme-provider";
import { useCan } from "../_lib/rbac/use-can";
import { RecentEncountersSection } from "../_lib/recent-encounters-section";
import { NewScanRequestSheet } from "../_lib/scan-request/new-scan-request-sheet";
import { startOrderedScan } from "../_lib/scan-request/scan-request-operations";
import { useDefaultDevice } from "../_lib/use-default-device";
import {
  useAllProbeAssociations,
  useMyProbeAssociations,
} from "../_lib/use-probe-associations";
import { linkToEpocusEncounter, linkToEpocusNewEncounter } from "../links";
import { IncompleteStudiesSection } from "./_lib/incomplete-studies-section";
import { ReviewerHomeSections } from "./_lib/reviewer-home";
import { ToPerformSection } from "./_lib/to-perform-section";
import { useHomeStats } from "./_lib/use-home-stats";
import {
  type ToDocumentEntry,
  useToDocumentQueue,
} from "./_lib/use-to-document-queue";
import { useToPerformQueue } from "./_lib/use-to-perform-queue";

// ─── Layout ──────────────────────────────────────────────────────────────────

const PageRoot = styled(Box)({
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 20,
  [epocusTheme.breakpoints.up("sm")]: {
    padding: 24,
    gap: 24,
  },
});

const SectionHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 10,
});

const SectionTitle = styled(Typography)({
  fontSize: rem(16),
  fontWeight: 700,
  color: EPOCUS_TEXT_PRIMARY,
});

// ─── Greeting ─────────────────────────────────────────────────────────────────

const GreetingTitle = styled(Typography)({
  fontSize: rem(22),
  fontWeight: 700,
  color: EPOCUS_TEXT_PRIMARY,
  lineHeight: 1.2,
});

const GreetingSubtitle = styled(Typography)({
  fontSize: rem(14),
  color: EPOCUS_TEXT_SECONDARY,
  marginTop: 2,
});

const GreetingRow = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 12,
  [epocusTheme.breakpoints.up("sm")]: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
});

const NewEncounterButton = styled(Button)({
  minHeight: 44,
  borderRadius: 10,
  fontSize: rem(14),
  fontWeight: 600,
  textTransform: "none",
  alignSelf: "flex-start",
});

const RequestScanButton = styled(NewEncounterButton)({
  borderColor: EPOCUS_PRIMARY,
  color: EPOCUS_PRIMARY,
});

const ActionRow = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
});

const getGreeting = (): string => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

const getFirstName = (name: string | undefined): string => {
  if (!name) return "Doctor";
  // "Dr. Sarah Chen" → "Dr. Chen", or just first word for simple names
  const parts = name.split(" ");
  if (parts[0]?.toLowerCase().startsWith("dr")) {
    return parts.length > 1 ? `Dr. ${parts[parts.length - 1]}` : parts[0];
  }
  return parts[0] ?? name;
};

const GreetingSection: FC<{
  variant: EpocusRoleVariant;
  canRequestScan: boolean;
  onRequestScan: () => void;
}> = ({ variant, canRequestScan, onRequestScan }) => {
  const { user: auth0User } = useAuth0();
  const navigate = useNavigate();
  const name = getFirstName(auth0User?.name);

  return (
    <GreetingRow>
      <Box>
        <GreetingTitle>
          {getGreeting()}, {name}
        </GreetingTitle>
        <GreetingSubtitle>
          {variant === "reviewer"
            ? "Your review queue and QA activity"
            : "Here's your POCUS activity overview"}
        </GreetingSubtitle>
      </Box>
      <ActionRow>
        {canRequestScan && (
          <RequestScanButton
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            onClick={onRequestScan}
          >
            Request scan
          </RequestScanButton>
        )}
        {variant === "performer" && (
          <NewEncounterButton
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate(linkToEpocusNewEncounter())}
          >
            New Encounter
          </NewEncounterButton>
        )}
      </ActionRow>
    </GreetingRow>
  );
};

// ─── Stats ────────────────────────────────────────────────────────────────────

const StatsGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 10,
});

const StatCard = styled(Box)({
  backgroundColor: EPOCUS_WHITE,
  borderRadius: 12,
  padding: "14px 16px",
  border: `1px solid ${EPOCUS_BORDER}`,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-end",
});

const StatCardLeft = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 4,
});

const STAT_LABELS = [
  "TODAY'S SCANS",
  "PENDING REVIEW",
  "TOTAL ENCOUNTERS",
  "AVG QA SCORE",
] as const;
const StatValue = styled(Typography)({
  fontSize: rem(28),
  fontWeight: 700,
  color: EPOCUS_TEXT_PRIMARY,
  lineHeight: 1,
});

const StatLabel = styled(Typography)({
  fontSize: rem(11),
  fontWeight: 600,
  letterSpacing: "0.06em",
  color: EPOCUS_TEXT_SECONDARY,
});

const StatsSection: FC = () => {
  const { todayCount, pendingCount, totalCount, avgQaScore } = useHomeStats();
  const values = [todayCount, pendingCount, totalCount, avgQaScore] as const;

  return (
    <StatsGrid>
      {STAT_LABELS.map((label, i) => {
        const StatIconComponent = STAT_ICON_COMPONENTS[i];
        return (
          <StatCard key={label}>
            <StatCardLeft>
              <StatLabel>{label}</StatLabel>
              <StatValue>{values[i]}</StatValue>
            </StatCardLeft>
            <StatIconComponent
              width={22}
              height={22}
              fill={EPOCUS_ICON_ACTIVE}
              aria-hidden="true"
              style={{ opacity: 0.6, flexShrink: 0 }}
            />
          </StatCard>
        );
      })}
    </StatsGrid>
  );
};

// ─── To Document ──────────────────────────────────────────────────────────────

const ToDocCountBadge = styled(Box)({
  backgroundColor: EPOCUS_ORANGE,
  color: EPOCUS_WHITE,
  borderRadius: 10,
  fontSize: rem(12),
  fontWeight: 700,
  minWidth: 20,
  height: 20,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "0 6px",
});

const ToDocCard = styled(Box)({
  backgroundColor: EPOCUS_TO_DOC_BG,
  border: `1px solid ${EPOCUS_TO_DOC_BORDER}`,
  borderRadius: 12,
  padding: "12px 14px",
  display: "flex",
  flexDirection: "column",
  gap: 10,
  [epocusTheme.breakpoints.up("sm")]: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
});

const ToDocCardTop = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 12,
  flex: 1,
  minWidth: 0,
});

const ToDocLabelRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  minWidth: 0,
});

const ToDocIcon = styled(Box)({
  width: 44,
  height: 44,
  borderRadius: 10,
  backgroundColor: EPOCUS_WHITE,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  border: `1px solid ${EPOCUS_BORDER}`,
});

const ToDocInfo = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: 3,
});

const ToDocLabel = styled(Typography)({
  fontSize: rem(15),
  fontWeight: 700,
  color: EPOCUS_TEXT_PRIMARY,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const ToDocDetail = styled(Typography)({
  fontSize: rem(12),
  color: EPOCUS_TEXT_SECONDARY,
});

const ClaimableBadge = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  backgroundColor: EPOCUS_STATUS_IMPORTED_BG,
  color: EPOCUS_STATUS_IMPORTED_TEXT,
  fontSize: rem(10),
  fontWeight: 700,
  letterSpacing: "0.06em",
  padding: "1px 6px",
  borderRadius: 4,
  marginLeft: 6,
  flexShrink: 0,
});

const DocumentNowButton = styled(Button)({
  backgroundColor: EPOCUS_ORANGE,
  color: EPOCUS_WHITE,
  borderRadius: 8,
  fontSize: rem(12),
  fontWeight: 700,
  textTransform: "none",
  minHeight: 36,
  minWidth: 44,
  whiteSpace: "nowrap",
  flexShrink: 0,
  alignSelf: "flex-end",
  "&:hover": {
    backgroundColor: EPOCUS_ORANGE_HOVER,
  },
  [epocusTheme.breakpoints.up("sm")]: {
    alignSelf: "center",
  },
});

const ToDocEmptyState = styled(Box)({
  padding: "24px 0",
  textAlign: "center",
});

const formatTriageTime = (isoDate: string): string => {
  const d = new Date(isoDate);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ToDocumentSection: FC<{
  entries: ReadonlyArray<ToDocumentEntry>;
  loading: boolean;
  onOpen: (encounterId: string) => void;
}> = ({ entries, loading, onOpen }) => {
  return (
    <Box>
      <SectionHeader>
        <SectionTitle>To Document</SectionTitle>
        {entries.length > 0 && (
          <ToDocCountBadge>{entries.length}</ToDocCountBadge>
        )}
      </SectionHeader>
      {loading && entries.length === 0 ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={24} style={{ color: EPOCUS_PRIMARY }} />
        </Box>
      ) : entries.length === 0 ? (
        <ToDocEmptyState>
          <Typography
            style={{ fontSize: rem(14), color: EPOCUS_TEXT_SECONDARY }}
          >
            Nothing to document right now
          </Typography>
        </ToDocEmptyState>
      ) : (
        <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
          {entries.map(({ encounter, claimable }) => {
            const detailParts = [
              formatTriageTime(encounter.date),
              encounter.deviceId,
            ].filter(Boolean);
            return (
              <ToDocCard key={encounter.id}>
                <ToDocCardTop>
                  <ToDocIcon>
                    <UltrasoundScanner
                      width={24}
                      height={24}
                      fill={EPOCUS_ICON_ACTIVE}
                      aria-hidden="true"
                    />
                  </ToDocIcon>
                  <ToDocInfo>
                    <ToDocLabelRow>
                      <ToDocLabel>
                        {encounterDisplayLabel(encounter)}
                      </ToDocLabel>
                      {claimable && <ClaimableBadge>UNCLAIMED</ClaimableBadge>}
                    </ToDocLabelRow>
                    <ToDocDetail>{detailParts.join(" · ")}</ToDocDetail>
                  </ToDocInfo>
                </ToDocCardTop>
                <DocumentNowButton
                  variant="contained"
                  disableElevation
                  onClick={() => onOpen(encounter.id)}
                >
                  Document Now
                </DocumentNowButton>
              </ToDocCard>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export const EpocusHomePage: FC = () => {
  const navigate = useNavigate();
  const user = useUser();
  const { getCurrentTime } = useServerTime();
  const variant = useRoleVariant();
  const encounterCollection = useRxCollectionEpocusEncounter();
  const examCollection = useRxCollectionEpocusExam();
  const defaultDeviceId = useDefaultDevice();
  const [requestSheetOpen, setRequestSheetOpen] = useState(false);
  const [startingId, setStartingId] = useState<string | null>(null);
  const { result: encounters } = useRxData<EpocusEncounter>(
    RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
    (collection) => collection?.find(),
  );

  const canClaimTriage = useCan("claimTriageEncounter");
  const canRequestScan = useCan("requestScan");
  const canStartOrderedScan = useCan("startOrderedScan");
  const { entries: toPerform } = useToPerformQueue({
    enabled: canStartOrderedScan,
  });

  const { associations } = useMyProbeAssociations();
  const { associations: allAssociations } = useAllProbeAssociations();
  const probeSerials = useMemo(
    () => associations.map((association) => association.deviceSerialNumber),
    [associations],
  );
  const allProbeSerials = useMemo(
    () => allAssociations.map((association) => association.deviceSerialNumber),
    [allAssociations],
  );
  const [serialRestrictedTriage, { loading: flagLoading }] = useFeatureFlag(
    featureFlags.epocus.serialRestrictedTriage,
  );
  const { entries, loading: triageLoading } = useToDocumentQueue({
    enabled: canClaimTriage,
    probeSerials,
    allProbeSerials,
    restrictToLinkedSerials: flagLoading || Boolean(serialRestrictedTriage),
  });

  const handleStartScan = async (encounterId: string) => {
    if (!encounterCollection || !examCollection) return;
    setStartingId(encounterId);
    try {
      await startOrderedScan({
        encounterCollection,
        examCollection,
        encounterId,
        userId: user.id,
        deviceId: defaultDeviceId,
        now: getCurrentTime().toISOString(),
      });
      navigate(linkToEpocusEncounter(encounterId));
    } catch (error) {
      toast.error("Couldn't start the scan. Please try again.");
      Sentry.captureException(error);
    } finally {
      setStartingId(null);
    }
  };

  const requestSheet = requestSheetOpen ? (
    <NewScanRequestSheet open onClose={() => setRequestSheetOpen(false)} />
  ) : null;

  if (variant === "reviewer") {
    return (
      <PageRoot>
        <GreetingSection
          variant={variant}
          canRequestScan={canRequestScan}
          onRequestScan={() => setRequestSheetOpen(true)}
        />
        <IncompleteStudiesSection />
        <ReviewerHomeSections />
        {requestSheet}
      </PageRoot>
    );
  }

  return (
    <PageRoot>
      <GreetingSection
        variant={variant}
        canRequestScan={canRequestScan}
        onRequestScan={() => setRequestSheetOpen(true)}
      />
      <IncompleteStudiesSection />
      <StatsSection />
      <ToPerformSection
        entries={toPerform}
        onStart={handleStartScan}
        startingId={startingId}
      />
      {canClaimTriage && (
        <ToDocumentSection
          entries={entries}
          loading={triageLoading}
          onOpen={(encounterId) => navigate(linkToEpocusEncounter(encounterId))}
        />
      )}
      <RecentEncountersSection encounters={encounters} />
      {requestSheet}
    </PageRoot>
  );
};
